import {
  MapPin,
  Building2,
  Plane,
  Train,
  Car,
  Home,
  Building
} from "lucide-react";

/**
 * Location type configuration mapping
 */
export const LOCATION_TYPE_CONFIG = {
  "city": {
    icon: Building2,
    label: "City",
    color: "bg-blue-100 text-blue-800",
    iconColor: "text-blue-600"
  },
  "in-terminal": {
    icon: Plane,
    label: "In-Terminal",
    color: "bg-green-100 text-green-800",
    iconColor: "text-green-600"
  },
  "Off-Terminal": {
    icon: Car,
    label: "Off-Terminal",
    color: "bg-orange-100 text-orange-800",
    iconColor: "text-orange-600"
  },
  "station": {
    icon: Train,
    label: "Station",
    color: "bg-purple-100 text-purple-800",
    iconColor: "text-purple-600"
  },
  "airport": {
    icon: Plane,
    label: "Airport",
    color: "bg-sky-100 text-sky-800",
    iconColor: "text-sky-600"
  },
  "hotel": {
    icon: Building,
    label: "Hotel",
    color: "bg-pink-100 text-pink-800",
    iconColor: "text-pink-600"
  },
  "downtown": {
    icon: Home,
    label: "Downtown",
    color: "bg-indigo-100 text-indigo-800",
    iconColor: "text-indigo-600"
  }
};

/**
 * Get location type configuration
 * @param {string} locationType - The location type from API
 * @returns {Object} Configuration object with icon, label, and colors
 */
export const getLocationTypeConfig = (location) => {
  // 1. Handle the NEW API response (is_airport boolean)
  if (location && typeof location.is_airport !== 'undefined') {
    if (location.is_airport === true) {
      return LOCATION_TYPE_CONFIG["airport"]; // This returns the Plane icon
    }
    return LOCATION_TYPE_CONFIG["city"]; // This returns Building2
  }

  // 2. Fallback for the OLD API / locationType string
  const type = location?.locationType || "";
  const normalizedType = type.toLowerCase().replace(/\s+/g, '-');

  if (LOCATION_TYPE_CONFIG[normalizedType]) {
    return LOCATION_TYPE_CONFIG[normalizedType];
  }

  // 3. Absolute Fallback
  return {
    icon: MapPin,
    label: "Location",
    color: "bg-gray-100 text-gray-800",
    iconColor: "text-gray-600"
  };
};
/**
 * Format location with type badge
 * @param {Object} location - Location object from API
 * @param {boolean} showBadge - Whether to show the type badge
 * @returns {Object} Formatted location object with display text and type info
 */
export const formatLocationWithType = (location, showBadge = true) => {
  if (!location) return { mainText: "", subText: "", displayText: "", typeConfig: null };

  // 1. Main Text: "Toronto (YYZ) - Pearson Intl Airport"
  // Removed the comma after city and structured it like a clean header
  const mainText = `${location.city}${location.gdsCode ? ` (${location.gdsCode})` : ''} - ${location.name}`;

  // 2. Sub Text: Only show country (and province/state if you have it)
  // We remove location.city from here to avoid repetition
  const subText = [location.country].filter(Boolean).join(", ");

  // 3. Selection Text: What stays in the box after clicking.
  // Intentionally excludes the country — the code alone (e.g. "(MCO)") is
  // enough to disambiguate, and this needs to match the summary bar's
  // shortenLocation() output, which also drops the country.
  const displayText = `${location.name}${location.gdsCode ? ` (${location.gdsCode})` : ''}, ${location.country}`;

  const typeConfig = showBadge ? getLocationTypeConfig(location) : null;

  return { mainText, subText, displayText, typeConfig, location };
};
